import React, {useEffect, useMemo} from "react";
import {NavLink, useLocation} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faTimes} from "@fortawesome/pro-regular-svg-icons/faTimes";

import Button from "../Button/Button";
import RenderIf from "../RenderIf/RenderIf";
import {toggleSidebarMenu} from "@/helpers/toggleSidebarMenu.js";
import {myAccountRoutes as myAccountDefaultRoutes} from "@/constants/menuRoutes";

import styles from "./SidebarMenu.module.scss";
import {useMediaQuery} from "@/hooks/useMediaQuery/useMediaQuery";
import SidebarDropdown from "./SidebarDropdown/SidebarDropdown";

import {useWhiteLabelTheme} from "@/hooks/useWhiteLabelTheme/useWhiteLabelTheme";
import {isEmpty} from "es-toolkit/compat";
import {
  faBoxCheck,
  faGear,
  faNewspaper,
  faQuestion,
  faReceipt,
  faRepeat,
  faShieldHeart,
  faTrashXmark,
  faUser,
} from "@fortawesome/pro-regular-svg-icons";

const ICON_MAP = {
  faUser: faUser,
  faGear: faGear,
  faRepeat: faRepeat,
  faBoxCheck: faBoxCheck,
  faNewspaper: faNewspaper,
  faShieldHeart: faShieldHeart,
  faTrashXmark: faTrashXmark,
  faReceipt: faReceipt,
};

export default function SidebarMenu() {
  const {pathname} = useLocation();
  const {themeConfig} = useWhiteLabelTheme();
  const {isDesktop, isTablet} = useMediaQuery();
  const showSidebarMenu = isDesktop || isTablet;

  useEffect(() => {
    if (!showSidebarMenu) {
      toggleSidebarMenu({isOpen: false});
    }

    return () => {
      toggleSidebarMenu({isOpen: false, showAnimation: false});
    };
  }, [showSidebarMenu]);

  const handleClick = path => {
    if (!path) return;

    if (path === pathname) {
      toggleSidebarMenu({isOpen: false});
    }
  };

  const myAccountRoutes = useMemo(() => {
    if (!isEmpty(themeConfig?.accountMenuNav)) {
      return themeConfig?.accountMenuNav.map((item, index) => ({
        ...item,
        icon: item.icon ? ICON_MAP[item.icon] || faQuestion : null,
        activeIcon: item.activeIcon ? ICON_MAP[item.activeIcon] || faQuestion : null,
        order: item.order || index + 1,
      }));
    }

    return myAccountDefaultRoutes;
  }, [myAccountDefaultRoutes]);

  return (
    <>
      <aside id="sidebarMenu" className={`${styles.container}`} aria-hidden="true">
        <div className={`${styles.content} container gx-0`}>
          <header className="row mx-0 align-items-center">
            <div className="col ps-5 text-center">
              <h3>My Account</h3>
            </div>
            <div className="col-auto g-0">
              <Button variant="transparent" className={`p-3 ${styles.closeBtn}`} onClick={toggleSidebarMenu}>
                <FontAwesomeIcon icon={faTimes} className="fa-fw" size="xl" />
              </Button>
            </div>
          </header>
          <nav>
            <ul id="parentItems" className={"list-unstyled pb-4"}>
              {myAccountRoutes
                .filter(route => route.show)
                .map(route => {
                  const path =
                    ["/delete"].includes(route.path) || route?.path?.includes("http")
                      ? route.path
                      : `/my-account/${route.path}`;

                  return (
                    <li key={route.title}>
                      <RenderIf isTrue={!route.subRoutes}>
                        <NavLink
                          to={path}
                          target={route?.path?.includes("http") ? "_blank" : "_self"}
                          onClick={() => handleClick(path)}
                          className={({isActive}) => {
                            return isActive ? `${styles.navItem} ${styles.active}` : `${styles.navItem}`;
                          }}>
                          <FontAwesomeIcon icon={route.icon} />
                          {route.title}
                        </NavLink>
                      </RenderIf>
                      <RenderIf isTrue={route.subRoutes}>
                        <SidebarDropdown route={route} />
                      </RenderIf>
                    </li>
                  );
                })}
            </ul>
          </nav>
        </div>
      </aside>
      <div id="sidebarBackdrop" className={`${styles.backdrop}`} aria-hidden="true" onClick={toggleSidebarMenu}></div>
    </>
  );
}
